import React, { useState, useEffect, useMemo } from 'react';
import { Trip } from '../../../../redux/api/trips/trips.model';
import {
  closeInvoicePreview,
  closePdfViewer,
  closeTripDetail,
  closeEditLoad,
  closeLoadDetail,
  setTripDetailReload,
  setLoadDetailReload,
  openLoadDetail,
  closeSendDocuments,
  closeCreateInvoice,
  closeShareLoad,
  closeDeleteTripConfirmation,
  closeDeleteLoadConfirmation
} from '../../../../redux/features/navigation/navigationSlice';
import { updateTripId } from '../../../../redux/features/trip/tripSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import TwNotifyDriver, {
  INotifyDriverWithFullSegment
} from '../../../../components/TwNotifyDriver/TwNotifyDriver';
import { useLazyGetTripByIdQuery } from '../../../../redux/api/trips/tripApi';
import LoadsService from '../../../../providers/Loads/Loads.service';
import InvoicesService from '../../../../providers/Invoices/Invoices.service';
import { logRocketTrack } from '../../../../utils/logRocketUtils';
import { UserResponse } from '../../../../providers/User/User.model';
import { getDeliveredColumnStatusesByRole, LOAD_TYPE } from '../../../../redux/api/load/load.model';
import {
  clearLoadState,
  setDuplicateLoad,
  setInvoiceDivisionId,
  setLoadCalendarReRenderId,
  setOrderId
} from '../../../../redux/features/orders/ordersSlice';
import TwSendOrderDocs from '../../../../components/TwSendOrderDocs/TwSendOrderDocs';
import TwDocumentViewer, {
  TwDocumentType
} from '../../../../components/TwDocumentViewer/TwDocumentViewer';
import CreateInvoice from '../../../CreateInvoice/CreateInvoice';
import EditInvoice from '../../../EditInvoice/EditInvoice';
import TwSavePDF from '../../../../components/TwSavePDF/TwSavePDF';
import EditDuplicateOrder from '../../../EditDuplicateOrder/EditDuplicateOrder';
import EditTrip from '../../../EditTrip/EditTrip';
import TwEntityOverlay from '../../../../components/TwEntityOverlay/TwEntityOverlay';
import DeleteTrip from '../../../Trips/DeleteTrip/DeleteTrip';
import { EntityType } from '../../../../redux/Global.model';
import DeleteLoad from '../../../Loads/DeleteLoad/DeleteLoad';
import { ORDER_STATUS } from '../../../../redux/api/orders/orders.model';

interface TripOverlayExpensesProps {
  enableLtl: boolean;
  isUseRateTableAppSettingEnabled: boolean;
  userData?: UserResponse;
}

const TripOverlayExpenses: React.FC<TripOverlayExpensesProps> = ({
  enableLtl,
  isUseRateTableAppSettingEnabled,
  userData
}): JSX.Element => {
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();

  const isTripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);
  const tripId = useAppSelector((state) => state.trip.tripId);
  const isEditTripOpen = useAppSelector((state) => state.navigation.isEditTripOpen);
  const isEditLoadOpen = useAppSelector((state) => state.navigation.isEditLoadOpen);
  const {
    duplicate_load,
    invoice_load_ids,
    invoice_division_id,
    order_id: load_id
  } = useAppSelector((state) => state.orders);
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const isDeleteTripConfirmationOpen = useAppSelector(
    (state) => state.navigation.isDeleteTripConfirmationOpen
  );
  const isDeleteLoadConfirmationOpen = useAppSelector(
    (state) => state.navigation.isDeleteConfirmationOpen
  );
  const isSendDocumentsOpen = useAppSelector((state) => state.navigation.isSendDocumentsOpen);
  const isPdfViewerOpen = useAppSelector((state) => state.navigation.isPdfViewerOpen);
  const isCreateInvoiceOpen = useAppSelector((state) => state.navigation.isCreateInvoiceOpen);
  const isShareLoadOpen = useAppSelector((state) => state.navigation.isShareLoadOpen);
  const isInvoicePreviewOpen = useAppSelector((state) => state.navigation.isInvoicePreviewOpen);
  const selectedTripId = useAppSelector((state) => state.orders.selected_trip_id); // For FTL Order overlay
  const isCalendarOpen = useAppSelector((state) => state.navigation.isCalendarOpen);

  const [isEditInvoiceOpen, setEditInvoiceOpen] = useState<boolean>(false);
  const [invoiceIdToEdit, setInvoiceIdToEdit] = useState<string>('');
  const [shareLoadSuccess, setShareLoadSuccess] = useState<boolean>(false);
  const [isNormalShare, setIsNormalShare] = useState<boolean>(false);
  const [driversToNotify, setDriversToNotify] = useState<INotifyDriverWithFullSegment[]>([]);
  const [isSplitLoad, setIsSplitLoad] = useState<boolean>(false);
  const [tripReadableId, setTripReadableId] = useState<string | number | null>();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | undefined>();
  const [loadReadableId, setLoadReadableId] = useState<number | null>();
  const [invoice, setInvoice] = useState<Blob | undefined>(undefined);
  const [invoiceNumber, setInvoiceNumber] = useState<string>();
  const [isEditedInvoicePreviewOpen, setIsEditedInvoicePreviewOpen] = useState<boolean>(false);
  const [currentLoadStatus, setCurrentLoadStatus] = useState<ORDER_STATUS[]>([
    ORDER_STATUS.NOT_STARTED
  ]);

  const [getTripById] = useLazyGetTripByIdQuery();

  // #region useEffects
  useEffect(() => {
    (async (): Promise<void> => {
      if (!isInvoicePreviewOpen) return;
      try {
        const load = await LoadsService.getOrderById(load_id as string);
        if (!load) return;

        if (!load || !load?.invoice?.id || !load?.invoice?.invoice_id) return;
        const invoicePDF = await InvoicesService.getInvoicePdf(load?.invoice?.id as string);
        setLoadReadableId(load.readable_id);
        setInvoiceNumber(load.invoice?.invoice_id);
        setInvoice(invoicePDF);
      } catch {
        dispatch(closeInvoicePreview());
        logRocketTrack('Invoice', {
          action: 'Download invoice on load details page',
          severity: 'medium',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Error downloading invoice. Please try again.',
          variant: 'error'
        });
      }
    })();
  }, [isInvoicePreviewOpen]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (!isShareLoadOpen) return;
      try {
        let driversToNotify: INotifyDriverWithFullSegment[] = [];
        if (enableLtl) {
          const trip = await getTripById(tripId as string).unwrap();

          driversToNotify = getDriversToNotify([trip], '');
          setDriversToNotify(driversToNotify);
          driversToNotify.length <= 1 ? setIsNormalShare(true) : setIsNormalShare(false);
        } else {
          const load = await LoadsService.getOrderById(load_id as string);
          if (!load) return;
          setIsSplitLoad(load.order_type === LOAD_TYPE.SEGMENT);

          driversToNotify = getDriversToNotify(load?.trips || [], load.readable_id);
          driversToNotify.length <= 1 ? setIsNormalShare(true) : setIsNormalShare(false);

          if (selectedSegmentId) {
            const segment = load?.trips?.find((segment) => segment.id === selectedSegmentId);
            if (segment && segment.drivers.length > 0) {
              driversToNotify = getDriversToNotify([segment], load.readable_id);
              setIsNormalShare(false);
            }
          }

          setDriversToNotify(driversToNotify);
        }
      } catch {
        logRocketTrack('Load Build', {
          action: 'Select driver for load',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: `Couldn't get drivers list. Please try again`,
          variant: 'error'
        });
      }
    })();
  }, [isShareLoadOpen, selectedSegmentId, tripId]);

  useEffect(() => {
    if (!tripId || !isDeleteTripConfirmationOpen) return;

    (async (): Promise<void> => {
      try {
        const trip = await getTripById(tripId).unwrap();
        if (!trip) return;

        setTripReadableId(trip.readable_id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    })();
  }, [tripId, isDeleteTripConfirmationOpen]);

  useEffect(() => {
    if (!load_id || !isDeleteLoadConfirmationOpen) return;

    (async (): Promise<void> => {
      try {
        const load = await LoadsService.getOrderById(load_id as string);
        if (!load) return;

        setLoadReadableId(load.readable_id);

        const deliveredColumnStatuses = getDeliveredColumnStatusesByRole(userData?.role);
        setCurrentLoadStatus(
          deliveredColumnStatuses.includes(load.status) ? deliveredColumnStatuses : [load.status]
        );
      } catch (err) {
        console.error(err);
      }
    })();
  }, [load_id, isDeleteLoadConfirmationOpen]);

  const getDriversToNotify = (
    segments: Trip[],
    orderReadableId: string | number
  ): INotifyDriverWithFullSegment[] => {
    const driversToNotify: INotifyDriverWithFullSegment[] = [];

    segments.forEach((segment) => {
      segment.drivers.forEach((driver, driverIndex) => {
        driversToNotify.push({
          driverId: driver.id,
          driverIndex,
          fullSegment: { ...segment, order_readable_id: orderReadableId }
        });
      });
    });

    return driversToNotify;
  };

  /**
   * Triggers the PDF download if generated successfully
   */
  const onSavePDFSuccess = (pdf: Blob, loadReadableId: string, clientName: string): void => {
    const data = pdf && window.URL.createObjectURL(pdf);
    const link = document.createElement('a');
    link.href = data || '';
    link.download = `${clientName}-Order#${loadReadableId}.pdf`;
    link.click();
    setTimeout(function () {
      // For Firefox it is necessary to delay revoking the ObjectURL
      data && window.URL.revokeObjectURL(data);
    }, 100);

    dispatch(closePdfViewer());
  };

  // Trip overlay close
  const handleTripDetailClose = (): void => {
    dispatch(closeTripDetail());
    dispatch(updateTripId(undefined));
  };

  // Edit Load events
  const handleEditLoadClose = (invoiceToEdit: string, duplicatedLoadId?: string): void => {
    if (!!invoiceToEdit) {
      setInvoiceIdToEdit(invoiceToEdit);
      dispatch(closeEditLoad());
      setEditInvoiceOpen(true);

      setTimeout(() => {
        dispatch(closeLoadDetail());
      }, 200);
      return;
    }

    dispatch(closeEditLoad());

    if (duplicate_load) {
      dispatch(setDuplicateLoad(false));
    }

    if (!isLoadDetailOpen) {
      dispatch(clearLoadState());
    } else {
      dispatch(setTripDetailReload(true));
    }

    if (duplicatedLoadId) {
      dispatch(setOrderId(duplicatedLoadId));
      setTimeout(() => {
        if (isLoadDetailOpen) {
          dispatch(setLoadDetailReload(true));
        } else {
          dispatch(openLoadDetail());
        }
      }, 1000);
    }
  };

  const handleEditLoad = (): void => {
    if (duplicate_load) dispatch(setDuplicateLoad(false));
  };

  // Send load docs events
  const handleSendDocsClose = (): void => {
    dispatch(closeSendDocuments());
  };

  const onSendDocsSuccess = (): void => {
    dispatch(closeSendDocuments());
  };

  const handleCloseCreateInvoice = (): void => {
    dispatch(closeCreateInvoice());
  };

  const handleOpenEditInvoiceFromPreview = (invoiceId: string, divisionId: string): void => {
    setInvoiceIdToEdit(invoiceId);
    dispatch(setInvoiceDivisionId(divisionId));
    dispatch(closeInvoicePreview());
    setEditInvoiceOpen(true);
  };

  // Share load preview events
  const handleShareClose = (): void => {
    setShareLoadSuccess(true);
    dispatch(closeShareLoad());
    setDriversToNotify([]);
  };

  const handleSelectedSegmentChange = (segmentId: string | undefined): void => {
    setSelectedSegmentId(segmentId);
  };

  const handleCloseInvoice = (): void => {
    dispatch(closeInvoicePreview());
    setInvoice(undefined);
    setLoadReadableId(null);
    setInvoiceNumber('');
    setIsEditedInvoicePreviewOpen(false);
  };

  const handleCloseEditInvoice = (): void => {
    setEditInvoiceOpen(false);
  };

  // Save as PDF events
  const handleSavePDFClose = (): void => {
    dispatch(closePdfViewer());
  };

  // Order overlay close
  const handleLoadDetailClose = (): void => {
    setShareLoadSuccess(false);
    dispatch(closeLoadDetail());
    dispatch(setOrderId(undefined));
    setSelectedSegmentId(undefined);
    dispatch(clearLoadState());
  };

  const handleLoadStatusChange = (): void => {
    if (isCalendarOpen) {
      dispatch(setLoadCalendarReRenderId(load_id!));
    }
  };

  const isDocumentViewerOpen = useMemo(() => {
    return (
      (isInvoicePreviewOpen && invoice !== null) || (isEditedInvoicePreviewOpen && invoice !== null)
    );
  }, [isInvoicePreviewOpen, isEditedInvoicePreviewOpen, invoice]);

  return (
    <>
      {isSendDocumentsOpen && (
        <TwSendOrderDocs
          orderId={load_id || ''}
          open={isSendDocumentsOpen}
          onClose={handleSendDocsClose}
          onSuccess={onSendDocsSuccess}
        />
      )}

      {isShareLoadOpen && (
        <TwNotifyDriver
          open={isShareLoadOpen}
          orderId={load_id || ''}
          onClose={handleShareClose}
          isNormalShare={isNormalShare}
          driversToNotify={driversToNotify}
          unassignedDriversToNotify={[]}
          isSplitLoad={isSplitLoad}
        />
      )}

      {/** Invoice preview */}
      {isDocumentViewerOpen && (
        <TwDocumentViewer
          readableId={loadReadableId as number}
          files={null}
          blobType={TwDocumentType.pdf}
          blobTitle={invoiceNumber ? `Invoice #${invoiceNumber}` : 'Invoice'}
          blob={invoice}
          open={isDocumentViewerOpen}
          onClickClose={handleCloseInvoice}
        />
      )}

      {isCreateInvoiceOpen && (
        <CreateInvoice
          loadIds={invoice_load_ids}
          divisionId={invoice_division_id}
          open={isCreateInvoiceOpen}
          onClickClose={handleCloseCreateInvoice}
          onEditQBOInvoice={handleOpenEditInvoiceFromPreview}
        />
      )}

      {isEditInvoiceOpen && (
        <EditInvoice
          invoiceId={invoiceIdToEdit}
          divisionId={invoice_division_id}
          open={isEditInvoiceOpen}
          onClickClose={handleCloseEditInvoice}
          setEditInvoiceOpen={setEditInvoiceOpen}
          setViewInvoiceOpen={(): void => setIsEditedInvoicePreviewOpen(true)}
          setViewInvoiceBlob={(blob): void => setInvoice(blob)}
        />
      )}

      {isPdfViewerOpen && (
        <TwSavePDF
          orderId={load_id || ''}
          open={isPdfViewerOpen}
          onClose={handleSavePDFClose}
          onSuccess={onSavePDFSuccess}
        />
      )}

      {isEditLoadOpen && (
        <EditDuplicateOrder
          loadId={load_id || ''}
          open={isEditLoadOpen}
          onClose={handleEditLoadClose}
          onEditOrder={handleEditLoad}
          showRateTable={isUseRateTableAppSettingEnabled}
        />
      )}

      {enableLtl && isEditTripOpen && <EditTrip openEdit={isEditTripOpen} />}

      {/* Trip overlay */}
      {isTripDetailOpen && (
        <TwEntityOverlay
          entityId={tripId || ''}
          open={isTripDetailOpen}
          entityType={EntityType.TRIP}
          onClose={handleTripDetailClose}
          onSelectedTripChange={handleSelectedSegmentChange}
          shareOrderSuccess={shareLoadSuccess}
        />
      )}

      {/* Order overlay for FTL */}
      {isLoadDetailOpen && (
        <TwEntityOverlay
          entityId={load_id || ''}
          entityType={EntityType.ORDER}
          open={isLoadDetailOpen}
          onClose={handleLoadDetailClose}
          onOrderStatusChange={handleLoadStatusChange}
          selectedTripId={selectedTripId}
          shareOrderSuccess={shareLoadSuccess}
        />
      )}

      {/** Delete trip confirmation **/}
      {isDeleteTripConfirmationOpen && (
        <DeleteTrip
          open={isDeleteTripConfirmationOpen}
          tripReadableId={tripReadableId || undefined}
          tripId={tripId || ''}
          orderId={load_id || ''}
          onClose={(): void => {
            dispatch(closeDeleteTripConfirmation());
            if (isTripDetailOpen) {
              dispatch(closeTripDetail());
            }

            if (isLoadDetailOpen) {
              dispatch(closeLoadDetail());
            }
            dispatch(updateTripId(undefined));
          }}
        />
      )}

      {/** Delete load confirmation **/}
      {isDeleteLoadConfirmationOpen && (
        <DeleteLoad
          open={isDeleteLoadConfirmationOpen}
          loadNumber={loadReadableId || undefined}
          loadId={load_id || ''}
          onClose={(): void => {
            dispatch(closeDeleteLoadConfirmation());
            if (isLoadDetailOpen) {
              dispatch(closeLoadDetail());
            }
          }}
        />
      )}
    </>
  );
};

export default TripOverlayExpenses;
